// ─── Navigation im Stammbaum (Tastatur) ─────────────────────────────────────
//
// Hoch/Runter folgen der Verwandtschaft (Eltern bzw. Kinder), links/rechts
// bewegen sich räumlich in derselben Baumreihe – dort stehen Partner,
// Geschwister und Cousins nebeneinander, das entspricht dem, was man sieht.

import type { TreeNode } from '@/api/tree.api';

export interface NavPosition {
  x: number;
  y: number;
}

export interface TreeNavGraph {
  /** Kind-ID → Eltern-IDs */
  parents: Map<string, string[]>;
  /** Eltern-ID → Kinder-IDs */
  children: Map<string, string[]>;
}

export type NavDirection = 'up' | 'down' | 'left' | 'right';

/** Zwei Knoten gelten als „gleiche Reihe", wenn ihre y-Werte so nah beieinander liegen. */
const ROW_TOLERANCE = 40;

export function buildNavGraph(nodes: TreeNode[]): TreeNavGraph {
  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();

  for (const n of nodes) {
    for (const parentId of [n.primaryParentId, n.secondaryParentId]) {
      if (!parentId) continue;
      const ps = parents.get(n.id);
      if (ps) ps.push(parentId);
      else parents.set(n.id, [parentId]);

      const cs = children.get(parentId);
      if (cs) cs.push(n.id);
      else children.set(parentId, [n.id]);
    }
  }

  return { parents, children };
}

/** Aus einer Kandidatenliste den waagerecht nächstgelegenen Knoten wählen. */
function nearestByX(
  candidates: string[],
  from: NavPosition,
  positions: Map<string, NavPosition>,
): string | null {
  let best: string | null = null;
  let bestDx = Infinity;
  for (const id of candidates) {
    const pos = positions.get(id);
    if (!pos) continue; // nicht im aktuellen Layout (z.B. Teilbaum-Ansicht)
    const dx = Math.abs(pos.x - from.x);
    if (dx < bestDx) {
      bestDx = dx;
      best = id;
    }
  }
  return best;
}

/**
 * Räumlicher Nachbar: erst die nächstgelegene Reihe in der gesuchten Richtung
 * bestimmen, dort dann den waagerecht nächsten Knoten nehmen.
 */
function spatialVertical(
  from: NavPosition,
  positions: Map<string, NavPosition>,
  direction: 'up' | 'down',
): string | null {
  const wantAbove = direction === 'up';
  let rowY: number | null = null;

  for (const pos of positions.values()) {
    const isCandidate = wantAbove
      ? pos.y < from.y - ROW_TOLERANCE
      : pos.y > from.y + ROW_TOLERANCE;
    if (!isCandidate) continue;
    // Die dem Ausgangspunkt am nächsten liegende Reihe gewinnt.
    if (rowY === null || (wantAbove ? pos.y > rowY : pos.y < rowY)) rowY = pos.y;
  }
  if (rowY === null) return null;

  const inRow: string[] = [];
  for (const [id, pos] of positions) {
    if (Math.abs(pos.y - rowY) <= ROW_TOLERANCE) inRow.push(id);
  }
  return nearestByX(inRow, from, positions);
}

/** Nächster Knoten links bzw. rechts innerhalb derselben Reihe. */
function spatialHorizontal(
  from: NavPosition,
  positions: Map<string, NavPosition>,
  direction: 'left' | 'right',
): string | null {
  const wantLeft = direction === 'left';
  let best: string | null = null;
  let bestDx = Infinity;

  for (const [id, pos] of positions) {
    if (Math.abs(pos.y - from.y) > ROW_TOLERANCE) continue;
    const dx = pos.x - from.x;
    if (wantLeft ? dx >= 0 : dx <= 0) continue;
    const dist = Math.abs(dx);
    if (dist < bestDx) {
      bestDx = dist;
      best = id;
    }
  }
  return best;
}

/**
 * Nachbar in der gewünschten Richtung – oder null, wenn es dort nichts gibt.
 * Hoch/runter bevorzugen echte Eltern/Kinder und weichen sonst auf die
 * räumlich nächste Reihe aus (z.B. bei angeheirateten Personen ohne Eltern).
 */
export function findNeighbor(
  currentId: string,
  direction: NavDirection,
  graph: TreeNavGraph,
  positions: Map<string, NavPosition>,
): string | null {
  const from = positions.get(currentId);
  if (!from) return null;

  if (direction === 'up') {
    return (
      nearestByX(graph.parents.get(currentId) ?? [], from, positions) ??
      spatialVertical(from, positions, 'up')
    );
  }
  if (direction === 'down') {
    return (
      nearestByX(graph.children.get(currentId) ?? [], from, positions) ??
      spatialVertical(from, positions, 'down')
    );
  }
  return spatialHorizontal(from, positions, direction);
}
